export const FLOOR_CANVAS_SIZE = 1000;
/** Table footprint as share of normalized canvas (matches 150px at 100% zoom). */
export const FLOOR_TABLE_SIZE_RATIO = 150 / FLOOR_CANVAS_SIZE;

/** Logical canvas extent — grows when zoomed out below 100%. */
export function effectiveCanvasSize(zoom: number): number {
	return FLOOR_CANVAS_SIZE / Math.min(zoom, 1);
}

/** Table node size as a fraction of the content box at the current zoom. */
export function floorTableDisplayRatio(zoom: number): number {
	return FLOOR_TABLE_SIZE_RATIO * Math.min(zoom, 1);
}
export const FLOOR_ZOOM_MIN = 0.5;
export const FLOOR_ZOOM_MAX = 2.5;
export const FLOOR_ZOOM_STEP = 0.25;
export const FLOOR_ZOOM_DEFAULT = 1;
export const DEFAULT_TABLE_W = 100;
export const DEFAULT_TABLE_H = 80;
export const DEFAULT_FLOOR_GRID = 20;

import { resolveTableTemplate } from "@lib/common";

type Table = T.Schema.Restaurant.Services.DineInTable;
type TableShape = T.Schema.Restaurant.Services.DineInTableShape;

export function tableLayoutDimensions(t: Table): { w: number; h: number } {
	const template = resolveTableTemplate(t);
	let w = t.layout?.w && t.layout.w > 0 ? t.layout.w : template.defaultW;
	let h = t.layout?.h && t.layout.h > 0 ? t.layout.h : template.defaultH;
	// Bump legacy small layouts up to current template minimums for floor visibility.
	w = Math.max(w, template.defaultW);
	h = Math.max(h, template.defaultH);
	const shape = t.shape || template.shape;
	if (shape === "square" || shape === "circle") {
		const side = Math.max(w, h);
		w = side;
		h = side;
	}
	return { w, h };
}

export function tableShapeBorderRadius(shape?: TableShape): string {
	if (shape === "circle") return "50%";
	return "10px";
}

export function snapCoord(value: number, grid: number): number {
	const g = grid > 0 ? grid : DEFAULT_FLOOR_GRID;
	return Math.round(value / g) * g;
}

export function clampTablePosition(
	x: number,
	y: number,
	w: number,
	h: number,
	canvasSize: number = FLOOR_CANVAS_SIZE
): { x: number; y: number } {
	const maxX = canvasSize - w;
	const maxY = canvasSize - h;
	return {
		x: Math.max(0, Math.min(maxX, x)),
		y: Math.max(0, Math.min(maxY, y)),
	};
}

/** Assign grid positions to tables missing layout (first open of floor editor). */
export function ensureTableLayouts(tables: Table[]): { tables: Table[]; changed: boolean } {
	let changed = false;
	const cols = 3;
	const gap = 40;
	const margin = 48;
	const next = tables.map((t, i) => {
		if (
			t.layout &&
			typeof t.layout.x === "number" &&
			typeof t.layout.y === "number"
		) {
			return t;
		}
		changed = true;
		const col = i % cols;
		const row = Math.floor(i / cols);
		const { defaultW, defaultH } = resolveTableTemplate(t);
		return {
			...t,
			layout: {
				x: margin + col * (defaultW + gap),
				y: margin + row * (defaultH + gap),
				w: defaultW,
				h: defaultH,
			},
		};
	});
	return { tables: next, changed };
}

export function clientPointToCanvasNorm(
	clientX: number,
	clientY: number,
	rect: DOMRect
): { x: number; y: number } {
	const x = ((clientX - rect.left) / rect.width) * FLOOR_CANVAS_SIZE;
	const y = ((clientY - rect.top) / rect.height) * FLOOR_CANVAS_SIZE;
	return { x, y };
}

export function tableCenter(t: Table): { x: number; y: number } {
	const { w, h } = tableLayoutDimensions(t);
	const lx = t.layout?.x ?? 0;
	const ly = t.layout?.y ?? 0;
	return { x: lx + w / 2, y: ly + h / 2 };
}

/** Connected components from explicit `joinable_with` links (2+ tables only). */
export function collectJoinGroups(tables: Table[]): string[][] {
	const parent = new Map<string, string>();
	const find = (id: string): string => {
		let root = id;
		while (parent.get(root) !== root) root = parent.get(root)!;
		let cur = id;
		while (cur !== root) {
			const next = parent.get(cur)!;
			parent.set(cur, root);
			cur = next;
		}
		return root;
	};
	const unite = (a: string, b: string) => {
		const ra = find(a);
		const rb = find(b);
		if (ra !== rb) parent.set(ra, rb);
	};
	for (const t of tables) parent.set(t._id, t._id);
	for (const [a, b] of collectExplicitJoinLinks(tables)) unite(a, b);
	const groups = new Map<string, string[]>();
	for (const t of tables) {
		const root = find(t._id);
		if (!groups.has(root)) groups.set(root, []);
		groups.get(root)!.push(t._id);
	}
	return [...groups.values()].filter((g) => g.length > 1);
}

export function joinGroupBoundsPct(
	groupIds: string[],
	tables: Table[],
	canvasSize: number,
	padding = 20
): { leftPct: number; topPct: number; widthPct: number; heightPct: number } | null {
	const byId = new Map(tables.map((t) => [t._id, t]));
	const ids = groupIds.filter((id) => byId.has(id));
	if (ids.length < 2) return null;
	let minX = Infinity;
	let minY = Infinity;
	let maxX = -Infinity;
	let maxY = -Infinity;
	for (const id of ids) {
		const t = byId.get(id)!;
		const { w, h } = tableLayoutDimensions(t);
		const x = t.layout?.x ?? 0;
		const y = t.layout?.y ?? 0;
		minX = Math.min(minX, x - padding);
		minY = Math.min(minY, y - padding);
		maxX = Math.max(maxX, x + w + padding);
		maxY = Math.max(maxY, y + h + padding);
	}
	return {
		leftPct: (minX / canvasSize) * 100,
		topPct: (minY / canvasSize) * 100,
		widthPct: ((maxX - minX) / canvasSize) * 100,
		heightPct: ((maxY - minY) / canvasSize) * 100,
	};
}

export function formatJoinGroupLabel(groupIds: string[], tables: Table[]): string {
	const byId = new Map(tables.map((t) => [t._id, t]));
	return groupIds
		.map((id, idx) => {
			const t = byId.get(id);
			return t?.name?.trim() || `Table ${idx + 1}`;
		})
		.join(" + ");
}

/** Unique explicit join pairs from `joinable_with` (both directions deduped). */
export function collectExplicitJoinLinks(tables: Table[]): Array<[string, string]> {
	const byId = new Set(tables.map((t) => t._id));
	const seen = new Set<string>();
	const out: Array<[string, string]> = [];
	for (const t of tables) {
		for (const other of t.joinable_with || []) {
			if (!byId.has(other)) continue;
			const key = [t._id, other].sort().join(",");
			if (seen.has(key)) continue;
			seen.add(key);
			out.push([t._id, other]);
		}
	}
	return out;
}

export const FLOOR_TABLE_ROTATION_STEP = 15;

export function tableSupportsRotation(t: Table): boolean {
	const shape = t.shape || resolveTableTemplate(t).shape;
	return shape === "rect";
}

export function tableLayoutRotation(t: Table): number {
	const r = t.layout?.rotation;
	if (typeof r !== "number" || !Number.isFinite(r)) return 0;
	return ((r % 360) + 360) % 360;
}

export function rotateTable(
	tables: Table[],
	tableId: string,
	deltaDeg: number = FLOOR_TABLE_ROTATION_STEP
): Table[] {
	return tables.map((t) => {
		if (t._id !== tableId || !tableSupportsRotation(t)) return t;
		const rotation =
			(((tableLayoutRotation(t) + deltaDeg) % 360) + 360) % 360;
		return {
			...t,
			layout: {
				...(t.layout || { x: 0, y: 0 }),
				rotation,
			},
		};
	});
}

export function toggleJoinLink(
	tables: Table[],
	fromId: string,
	toId: string
): Table[] {
	if (fromId === toId) return tables;
	return tables.map((t) => {
		if (t._id !== fromId) return t;
		const links = [...(t.joinable_with || [])];
		const idx = links.indexOf(toId);
		if (idx >= 0) links.splice(idx, 1);
		else links.push(toId);
		return {
			...t,
			joinable_with: links.length ? links : undefined,
		};
	});
}
